import type { KeyboardEvent, MouseEvent, MutableRefObject, ReactElement, ReactNode } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import classNames from 'classnames';

import ChevronLine from '@/assets/icons/chevron_line.svg?component';
import { Button, type ButtonSize, type ButtonVariant } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import { type MenuContextValue, useMenuContext } from './MenuContext';
import { handleMenuKey, handlePanelKey } from './menuTriggerKeys';

import styles from './Menu.module.scss';

interface MenuTriggerBaseProps {
    children?: ReactNode;
    /**
     * A Button variant; `field` for a trigger drawn like a form field; `sidebar` for the
     * full-width trigger in the dark sidebar; `unstyled` for a trigger that brings its own
     * look via `className`.
     */
    variant?: ButtonVariant | 'unstyled' | 'field' | 'sidebar';
    size?: ButtonSize;
    icon?: ReactNode;
    /** Draws a chevron after the label that turns over while the menu is open. */
    caret?: boolean;
    /** Stretches the trigger to its container, e.g. a fixed-width select. */
    fullWidth?: boolean;
    className?: string;
    /**
     * Replaces the generated id, which also names a `menu` popup; give its MenuContent an
     * `aria-label` then.
     */
    id?: string;
    /** Analytics fallback for a styled trigger without a `trackingId`, as on Button. */
    name?: string;
    title?: string;
    disabled?: boolean;
    'aria-labelledby'?: string;
    /** `false` for a panel trigger, which discloses rather than opens a menu. */
    'aria-haspopup'?: boolean;
    'data-testid'?: string;
    /** Analytics id for the click that opens the menu; styled triggers only. */
    trackingId?: string;
    onClick?: (event: MouseEvent<HTMLElement>) => void;
    onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
    /** Receives the trigger element, e.g. to hand focus back to it. */
    triggerRef?: MutableRefObject<HTMLElement | null>;
}

export type MenuTriggerProps = MenuTriggerBaseProps &
    ({ iconOnly?: false; 'aria-label'?: string } | { iconOnly: true; 'aria-label': string });

interface TriggerPrimitiveProps {
    menu: MenuContextValue;
    disabled: boolean;
    setRef: (element: HTMLButtonElement | null) => void;
    children: ReactElement;
}

/** Radix's trigger for the menu's kind, around the element that renders the button. */
function TriggerPrimitive(props: TriggerPrimitiveProps) {
    const { menu, disabled, setRef, children } = props;
    return menu.kind === 'menu' ? (
        <DropdownMenu.Trigger asChild disabled={disabled} ref={setRef}>
            {children}
        </DropdownMenu.Trigger>
    ) : (
        <Popover.Trigger asChild disabled={disabled} ref={setRef}>
            {children}
        </Popover.Trigger>
    );
}

/**
 * Opens and closes its Menu. In a `menu` it owns the opening keys, so the arrow keys and
 * Enter land on the first (or last) item whatever role the items have. In a `panel` Enter
 * and Space are the browser's click; a `list` also opens on the arrow keys and puts a
 * keyboard open on its first (or last) control. Escape is the popup's, wherever focus is.
 */
export function MenuTrigger(props: MenuTriggerProps) {
    const {
        children,
        variant = 'secondary',
        size,
        icon,
        caret = false,
        iconOnly = false,
        className,
        disabled = false,
        onKeyDown,
        triggerRef,
    } = props;
    const menu = useMenuContext('MenuTrigger');

    const setRef = (element: HTMLButtonElement | null) => {
        menu.triggerRef.current = element;
        if (triggerRef) {
            triggerRef.current = element;
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        onKeyDown?.(event);
        if (event.isPropagationStopped() || disabled) return;
        if (menu.kind === 'menu') {
            handleMenuKey(menu, event);
        } else {
            handlePanelKey(menu, event);
        }
    };

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        props.onClick?.(event);
        // Radix opens a menu as the pointer goes down. A click with no press behind it
        // comes from assistive technology or script, and the menu has always opened on
        // a click, so that one toggles it too. A panel opens on the click anyway.
        if (menu.kind === 'menu' && event.detail === 0 && !disabled) {
            menu.setOpen(!menu.isOpen);
        }
    };

    const chevron = caret ? (
        <SvgIcon
            icon={ChevronLine}
            size="small"
            color="currentColor"
            className={classNames(styles.chevron, { [styles.chevronOpen]: menu.isOpen })}
        />
    ) : null;

    const shared = {
        id: props.id ?? menu.triggerId,
        // Only while open: the popup is not in the document otherwise.
        'aria-controls': menu.isOpen ? menu.contentId : undefined,
        title: props.title,
        className,
        'aria-haspopup': props['aria-haspopup'] ?? true,
        'data-testid': props['data-testid'],
        onClick: handleClick,
        onKeyDown: handleKeyDown,
    };

    if (variant === 'unstyled' || variant === 'field' || variant === 'sidebar') {
        return (
            <TriggerPrimitive menu={menu} disabled={disabled} setRef={setRef}>
                <button
                    type="button"
                    {...shared}
                    className={classNames(
                        {
                            [styles.sidebarTrigger]: variant === 'sidebar',
                            [styles.fieldTrigger]: variant === 'field',
                            [styles.fieldTriggerFull]: variant === 'field' && props.fullWidth,
                        },
                        className
                    )}
                    aria-label={props['aria-label']}
                    aria-labelledby={props['aria-labelledby']}>
                    {icon}
                    {children}
                    {chevron}
                </button>
            </TriggerPrimitive>
        );
    }

    const label = props['aria-label'];
    return (
        <TriggerPrimitive menu={menu} disabled={disabled} setRef={setRef}>
            {iconOnly && label !== undefined ? (
                <Button
                    {...shared}
                    variant={variant}
                    size={size}
                    icon={icon}
                    iconEnd={chevron}
                    fullWidth={props.fullWidth}
                    name={props.name}
                    trackingId={props.trackingId}
                    iconOnly
                    aria-label={label}
                />
            ) : (
                <Button
                    {...shared}
                    variant={variant}
                    size={size}
                    icon={icon}
                    iconEnd={chevron}
                    fullWidth={props.fullWidth}
                    name={props.name}
                    trackingId={props.trackingId}
                    aria-label={label}>
                    {children}
                </Button>
            )}
        </TriggerPrimitive>
    );
}

export default MenuTrigger;
